import { useQuery } from 'react-query';
import { ContentSearchResult } from '@/components/organisms/content-search-card/content-search-card';
import { QueryId, STALE_TIME } from '@/integrations/content/utils';
import {
  ContentSearchHeaders,
  ContentSearchParameters,
} from '@/integrations/content-search/content-search.fetch';
import { Banner } from '@/integrations/content-search/content-search.types';
import getCulture from '@/libraries/getters/get-culture';
import getDomain from '@/libraries/getters/get-domain';
import getLocale from '@/libraries/getters/get-locale';

export interface UseQueryContentSearchData {
  banners: Banner[];
  items: ContentSearchResult[];
}

interface UseQueryContentSearchParameters extends ContentSearchParameters {}

interface UseQueryContentSearchHeaders extends ContentSearchHeaders {}

interface UseQueryContentSearchOptions {
  enabled?: boolean;
  initialData?: UseQueryContentSearchData;
}

export const useQueryContentSearch = (
  parameters: UseQueryContentSearchParameters,
  customHeaders: UseQueryContentSearchHeaders,
  options?: UseQueryContentSearchOptions,
) => {
  const locale = getLocale();

  const query = useQuery<UseQueryContentSearchData>(
    [QueryId.CONTENT_SEARCH, JSON.stringify(parameters), customHeaders, locale],
    async () => {
      const fetchResponse = await fetch('/api/content-search', {
        body: JSON.stringify({
          headers: customHeaders,
          parameters,
        }),
        headers: {
          ['Accept-Language']: getCulture(locale),
          ['Content-Type']: 'application/json',
          ['Ocp-Apim-Subscription-Key']: `${process.env.APIM_SUBSCRIPTION_KEY}`,
          ['X-Domain-Host']: getDomain(),
        },
        method: 'POST',
      })
        .then(response => {
          if (response.ok) return response.json();

          throw new Error(`${response.status} - ${response.statusText}`);
        })
        .catch(error => {
          console.error('contentSearch.useQueryContentSearch', error);
          throw error;
        });

      return fetchResponse;
    },
    {
      enabled: options?.enabled,
      initialData: options?.initialData,
      keepPreviousData: true,
      staleTime: STALE_TIME,
    },
  );

  return query;
};
